import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, fmtMoney, fmtDate, fmtDateTime, fmtNumber } from "@/lib/apiClient";
import StatusBadge from "@/components/StatusBadge";
import { useParams, Link, Routes, Route } from "react-router-dom";
import { ArrowLeft, FileText, ArrowUpRight, SignOut, TrendUp, TrendDown, CalendarBlank } from "@phosphor-icons/react";

export default function ClientPortal() {
    return (
        <Routes>
            <Route index element={<PortalHome />} />
            <Route path="campaigns/:id" element={<PortalCampaign />} />
        </Routes>
    );
}

function PortalShell({ children }) {
    const { user, logout } = useAuth();
    return (
        <div className="min-h-screen bg-white">
            <header className="border-b border-slate-200 px-6 md:px-12 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center font-display font-bold text-lg">P</span>
                    <div className="flex flex-col leading-tight">
                        <span className="font-display text-lg tracking-tight">Pulse</span>
                        <span className="overline" style={{ fontSize: 9 }}>Client portal</span>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="hidden md:flex flex-col items-end leading-tight">
                        <span className="text-sm font-medium">{user?.name}</span>
                        <span className="overline" style={{ fontSize: 9 }}>{user?.email}</span>
                    </div>
                    <button onClick={logout} data-testid="portal-logout" className="p-2 text-slate-500 hover:text-slate-900"><SignOut size={18} /></button>
                </div>
            </header>
            <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-16">{children}</div>
        </div>
    );
}

function PortalHome() {
    const [campaigns, setCampaigns] = useState([]);
    const [digest, setDigest] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const [c, d] = await Promise.all([api.get("/campaigns"), api.get("/digest/client").catch(() => ({ data: null }))]);
                setCampaigns(c.data);
                setDigest(d.data);
            } catch (e) {}
        })();
    }, []);
    return (
        <PortalShell>
            <div className="mb-10">
                <div className="overline mb-2">// Welcome</div>
                <h1 className="font-display text-4xl md:text-5xl tracking-tighter">
                    {digest?.client_org_name ? digest.client_org_name : "Your campaigns"}
                </h1>
                <p className="text-slate-500 mt-3 text-base max-w-xl">
                    Approved reports and analysis live here. Click any campaign to view & download.
                </p>
            </div>

            {digest && digest.total_campaigns > 0 && <ClientDigestSection digest={digest} />}

            <div className="mt-10 mb-3 overline">All campaigns ({campaigns.length})</div>
            <div className="divide-y divide-slate-200 border-t border-b border-slate-200">
                {campaigns.map((c) => (
                    <Link to={`/portal/campaigns/${c.id}`} key={c.id} className="flex items-center justify-between py-5 hover:bg-slate-50 px-2 -mx-2" data-testid={`portal-campaign-${c.id}`}>
                        <div className="flex items-start gap-5">
                            <span className="font-mono text-xs text-slate-400 mt-1">{(c.platforms || []).slice(0, 2).join(" / ") || "—"}</span>
                            <div>
                                <div className="font-display text-lg">{c.name}</div>
                                <div className="overline mt-1">{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <StatusBadge status={c.stage} />
                            <ArrowUpRight size={18} className="text-slate-400" />
                        </div>
                    </Link>
                ))}
                {campaigns.length === 0 && (
                    <div className="py-16 text-center text-sm text-slate-500">No campaigns to show yet.</div>
                )}
            </div>
        </PortalShell>
    );
}

function ClientDigestSection({ digest }) {
    const s = digest.pace_summary || {};
    const gainers = digest.top_gainers || [];
    const decliners = digest.top_decliners || [];
    const ends = digest.upcoming_ends || [];
    const reports = digest.recent_approved_reports || [];

    return (
        <section className="space-y-6" data-testid="client-digest">
            <div className="overline">// This week at a glance · {digest.week_start} → {digest.week_end}</div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                <DStat label="Total campaigns" value={fmtNumber(digest.total_campaigns)} sub="all stages" testid="cd-total" />
                <DStat label="Active" value={fmtNumber(s.total_active || 0)} sub={`${s.on_track || 0} on track`} testid="cd-active" />
                <DStat label="New reports" value={fmtNumber(reports.length)} sub="approved last 7d" testid="cd-reports" />
                <DStat label="Ending soon" value={fmtNumber(ends.length)} sub="next 14 days" testid="cd-ending" />
            </div>

            {(gainers.length > 0 || decliners.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                    <MoverPanel title="Strong performers" items={gainers} color="#15803D" Icon={TrendUp} testid="cd-gainers" />
                    <MoverPanel title="Needs attention" items={decliners} color="#B91C1C" Icon={TrendDown} testid="cd-decliners" />
                </div>
            )}

            {reports.length > 0 && (
                <div>
                    <div className="overline mb-2">Recently approved reports</div>
                    <ul className="divide-y divide-slate-200 border-t border-b border-slate-200">
                        {reports.slice(0, 5).map((r) => (
                            <li key={r.id} className="flex items-center justify-between py-3 px-1" data-testid={`cd-report-${r.id}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    <FileText size={16} className="text-slate-400" />
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">{r.campaign_name}</div>
                                        <div className="overline text-slate-400">{r.kind} · approved {fmtDateTime(r.approved_at || r.created_at)}</div>
                                    </div>
                                </div>
                                <Link to={`/portal/campaigns/${r.campaign_id}`} className="text-xs font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline inline-flex items-center gap-1">
                                    Open <ArrowUpRight size={12} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {ends.length > 0 && (
                <div>
                    <div className="overline mb-2">Ending soon</div>
                    <ul className="divide-y divide-slate-200 border-t border-b border-slate-200">
                        {ends.slice(0, 5).map((e) => (
                            <li key={e.id} className="flex items-center justify-between py-3 px-1" data-testid={`cd-ending-${e.id}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    <CalendarBlank size={16} className="text-slate-400" />
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">{e.name}</div>
                                        <div className="overline text-slate-400">{fmtDate(e.end_date)}</div>
                                    </div>
                                </div>
                                <span className={`font-mono text-xs ${e.days_to_end <= 3 ? "text-red-700" : "text-slate-700"}`}>
                                    {e.days_to_end === 0 ? "today" : `${e.days_to_end} day${e.days_to_end === 1 ? "" : "s"}`}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

function DStat({ label, value, sub, testid }) {
    return (
        <div className="bg-white p-5" data-testid={testid}>
            <div className="overline">{label}</div>
            <div className="font-mono text-2xl mt-1 tracking-tighter">{value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
        </div>
    );
}

function MoverPanel({ title, items, color, Icon, testid }) {
    return (
        <div className="bg-white p-5" data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="overline">WoW</div>
                    <h3 className="font-display text-base">{title}</h3>
                </div>
                <Icon size={18} style={{ color }} weight="bold" />
            </div>
            {items.length === 0 ? (
                <p className="text-sm text-slate-500">No movement yet.</p>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {items.map((m, i) => (
                        <li key={m.id} className="py-2 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <span className="font-mono text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                                <div className="text-sm truncate">{m.name}</div>
                            </div>
                            <span className="font-mono text-sm font-semibold" style={{ color }}>
                                {m.wow_percent >= 0 ? "+" : ""}{m.wow_percent.toFixed(0)}%
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function PortalCampaign() {
    const { id } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [reports, setReports] = useState([]);
    useEffect(() => {
        (async () => {
            try {
                const [c, r] = await Promise.all([
                    api.get(`/campaigns/${id}`),
                    api.get(`/reports/campaigns/${id}`),
                ]);
                setCampaign(c.data);
                setReports(r.data);
            } catch (e) {}
        })();
    }, [id]);

    const download = async (fileId, filename) => {
        const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a"); a.href = url; a.download = filename || fileId; a.click();
        URL.revokeObjectURL(url);
    };

    if (!campaign) return <PortalShell><div className="text-sm text-slate-400 font-mono">loading…</div></PortalShell>;

    const daily = reports.filter((r) => r.kind === "daily");
    const weekly = reports.filter((r) => r.kind === "weekly");
    const analysis = reports.filter((r) => r.kind === "analysis");

    return (
        <PortalShell>
            <Link to="/portal" className="overline inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 mb-6">
                <ArrowLeft size={12} /> All campaigns
            </Link>
            <div className="mb-10">
                <div className="overline mb-1">{campaign.client_org_name}</div>
                <h1 className="font-display text-4xl tracking-tighter">{campaign.name}</h1>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                    <StatusBadge status={campaign.stage} size="lg" />
                    <span className="overline">{fmtDate(campaign.start_date)} → {fmtDate(campaign.end_date)}</span>
                </div>
            </div>

            <Section title="Daily reports" reports={daily} download={download} />
            <Section title="Weekly reports" reports={weekly} download={download} />
            <Section title="24-hour analysis" reports={analysis} download={download} />
        </PortalShell>
    );
}

function Section({ title, reports, download }) {
    return (
        <section className="mb-10">
            <div className="overline mb-3">{title}</div>
            {reports.length === 0 ? (
                <div className="text-sm text-slate-400 border border-dashed border-slate-200 px-4 py-6">No approved reports yet.</div>
            ) : (
                <div className="divide-y divide-slate-200 border-t border-b border-slate-200">
                    {reports.map((r) => (
                        <div key={r.id} className="py-4 flex items-center justify-between" data-testid={`portal-report-${r.id}`}>
                            <div className="flex items-start gap-3">
                                <FileText size={18} className="text-slate-400 mt-0.5" />
                                <div>
                                    <div className="text-sm font-medium">{r.data?.title || `${r.kind} report`}</div>
                                    <div className="overline text-slate-400 mt-0.5">{fmtDateTime(r.created_at)}</div>
                                </div>
                            </div>
                            {r.data?.file_id && (
                                <button
                                    onClick={() => download(r.data.file_id, `${r.kind}-report.pdf`)}
                                    className="text-xs font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline"
                                    data-testid={`portal-download-${r.id}`}
                                >
                                    Download →
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
